import React, { useState, useEffect, useMemo } from 'react';
import RestaurantsFilterBar from '../../components/restaurants/RestaurantsFilterBar';
import RestaurantsMapView from '../../components/restaurants/RestaurantsMapView';
import RestaurantsList from '../../components/restaurants/RestaurantsList';
import RestaurantsLoadMoreButton from '../../components/restaurants/RestaurantsLoadMoreButton';
import { fetchAllRestaurants } from '../../lib/api';
import { applyFilters } from '../../lib/utils';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { readSessionJson, writeSessionJson } from '../../utils/storage';
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls';
import { getRuntimeSiteContext } from '../../lib/siteContext';

const PAGE_SIZE = 24;
const STORAGE_KEY = 'alakai_restaurants_index_state_v2';

const toArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
const toString = (value) => (typeof value === 'string' ? value : '');

const normalizeFilters = (raw) => ({
  region: toArray(raw?.region),
  serviceType: toArray(raw?.serviceType),
  priceRange: toArray(raw?.priceRange),
  cuisineCategory: toArray(raw?.cuisineCategory),
  searchText: toString(raw?.searchText),
});

const RestaurantsIndex = () => {
  const location = useLocation();
  const { island, islandConfig } = getRuntimeSiteContext();
  const storageKey = useMemo(() => `${STORAGE_KEY}_${island}`, [island]);
  const stored = useMemo(() => readSessionJson(storageKey), [storageKey]);
  const [all, setAll] = useState([]);
  const defaultFilters = useMemo(
    () => ({
      region: [], serviceType: [], priceRange: [], cuisineCategory: [], searchText: ''
    }),
    []
  );

  const [filters, setFilters] = useState(() => {
    const normalized = normalizeFilters(stored?.filters);
    return { ...defaultFilters, ...normalized };
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [mapBounds, setMapBounds] = useState(null);
  const mapViewRef = React.useRef(stored?.mapView ?? null);

  // Initialize filters from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const regionParam = searchParams.get('region');
    
    if (regionParam) {
      setFilters(prev => ({
        ...prev,
        region: [regionParam]
      }));
    }
  }, [location.search]);

  // Load all restaurants once, then shuffle to get a random selection
  useEffect(() => {
    fetchAllRestaurants().then(data => {
      const shuffled = data.sort(() => 0.5 - Math.random());
      setAll(shuffled);
    });
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    writeSessionJson(storageKey, {
      filters,
      mapView: mapViewRef.current,
      storedAt: Date.now(),
    });
  }, [filters, storageKey]);

  // 1) All items matching current filters
  const filtered = useMemo(
    () => applyFilters(all, filters),
    [all, filters]
  );

  // 2) Of those, only those within the map viewport
  const inViewport = useMemo(() => {
    if (!mapBounds) return filtered;
    return filtered.filter(r => {
      // use position (from MapView) if present, otherwise fall back to the original location field
      const { lat, lng } = r.position ?? r.location;
      return mapBounds.contains({ lat, lng });
    });
  }, [filtered, mapBounds]);

  // 3) Finally, slice for pagination
  const displayed = useMemo(
    () => inViewport.slice(0, currentPage * PAGE_SIZE),
    [inViewport, currentPage]
  );

  return (
	      <div className="restaurants-index-page pt-0 space-y-6">
	        <Helmet>
	          <title>{`${islandConfig.title} Restaurants | Alaka'i Aloha`}</title>
	          <meta
	            name="description"
	            content={`Browse ${islandConfig.title} restaurants with map view, filters, and local picks to plan your meals.`}
	          />
	          <link rel="canonical" href={buildRuntimeCanonicalUrl('/restaurants')} />
	        </Helmet>
	        <div className="flex justify-between items-center mb-2">
	          <h1 className="text-4xl font-bold">Restaurants</h1>
	          <Link
	            to="/restaurants/list"
            className="btn-secondary-compact"
            title="View a complete list of all restaurants"
          >
            View as list
          </Link>
        </div>

        <RestaurantsFilterBar
          filters={filters}
          onChange={setFilters}
        />

        <div className="mt-6">
          <RestaurantsMapView
            restaurants={displayed}
            onBoundsChanged={setMapBounds}
            initialCenter={mapViewRef.current?.center}
            initialZoom={mapViewRef.current?.zoom}
            onViewChanged={(nextView) => {
              mapViewRef.current = nextView;
              writeSessionJson(storageKey, {
                filters,
                mapView: nextView,
                storedAt: Date.now(),
              });
            }}
          />
        </div>

        <div className="mt-6">
          <RestaurantsList restaurants={displayed} />
        </div>

        {displayed.length < inViewport.length && (
          <RestaurantsLoadMoreButton
            onClick={() => setCurrentPage(p => p + 1)} />
        )}
      </div>
  );
};

export default RestaurantsIndex;
